/**
 * @fileoverview Persisted numeric UI preference hook — pane widths, split ratios.
 * @module ui-admin/hooks/usePersistedNumber
 *
 * Responsibilities: - Return [value, setValue] for a numeric preference backed
 *                     by localStorage (builder pane widths, preview splits).
 * Dependencies:     - React (useState, useCallback).
 * Architecture:     - Ephemeral UI preference only — never used for data.
 *                     SSR/private-mode safe (try/catch around storage access).
 * Notes:            - Extracted from DocumentTemplateBuilderPage.tsx and
 *                     SystemDocumentBuilderPage.tsx (was a per-page copy) so all
 *                     builder shells share one implementation (DRY rule).
 */

import { useCallback, useState } from 'react';

/**
 * @hook usePersistedNumber
 * Purpose:          Numeric UI preference persisted to localStorage.
 * Returns:          [value, setValue] — reads once on mount.
 * Side Effects:     Writes localStorage on every set.
 * Offline Behavior: fully local — storage failure falls back to `fallback`.
 * @storage localStorage key (e.g. doc-builder-panel-width); ephemeral UI pref only.
 * @errorHandling Falls back to `fallback` when storage is unavailable or the
 *   stored value is non-finite.
 */
export function usePersistedNumber(key: string, fallback: number): [number, (n: number) => void] {
  const [value, setValue] = useState<number>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
      const parsed = raw == null ? NaN : Number(raw);
      return Number.isFinite(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  });
  const set = useCallback((n: number) => {
    setValue(n);
    try { window.localStorage.setItem(key, String(n)); } catch { /* ignore quota/availability */ }
  }, [key]);
  return [value, set];
}
